import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import type { Field, Recnum, UUID } from '@certhon/models';
import { Check, ChevronDown, ChevronRight, Megaphone } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useCreateFeedbackMutation } from '../feedback.queries';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface FeedbackSectionProps {
  field: Field;
  listId: UUID;
  userId: Recnum;
  disabled?: boolean;
}

// ---------------------------------------------------------------------------
// FeedbackSection
// ---------------------------------------------------------------------------

/**
 * Collapsible "Internal feedback" section that lets technicians submit
 * feedback about a template question. Only renders when the field has a
 * protoChain (i.e., it was expanded from a template).
 */
export function FeedbackSection({
  field,
  listId,
  userId,
  disabled = false,
}: FeedbackSectionProps) {
  const [expanded, setExpanded] = useState(false);
  const [message, setMessage] = useState('');
  const [showThankYou, setShowThankYou] = useState(false);
  const thankYouTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const createFeedback = useCreateFeedbackMutation();

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (thankYouTimer.current) clearTimeout(thankYouTimer.current);
    };
  }, []);

  // Reset state when the field changes
  useEffect(() => {
    setExpanded(false);
    setMessage('');
    setShowThankYou(false);
    if (thankYouTimer.current) {
      clearTimeout(thankYouTimer.current);
      thankYouTimer.current = null;
    }
  }, [field.id]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const protoRef = field.protoChain[0];
      if (!message.trim() || disabled || !protoRef) return;

      createFeedback.mutate(
        {
          message: message.trim(),
          user_id: userId,
          proto_list_id: protoRef.listId,
          proto_node_id: protoRef.nodeId,
          instance_list_id: listId,
          instance_node_id: field.id,
        },
        {
          onSuccess: () => {
            setMessage('');
            setExpanded(false);
            setShowThankYou(true);
            thankYouTimer.current = setTimeout(() => {
              setShowThankYou(false);
            }, 3000);
          },
        },
      );
    },
    [message, disabled, createFeedback, userId, field, listId],
  );

  const handleCancel = useCallback(() => {
    setMessage('');
    setExpanded(false);
  }, []);

  // Don't render if there is no proto chain (field was not expanded from a template)
  if (field.protoChain.length === 0) {
    return null;
  }

  return (
    <div className="mt-6 border-t pt-4">
      {/* Toggle header */}
      <button
        type="button"
        onClick={() => {
          if (!disabled) setExpanded((prev) => !prev);
        }}
        disabled={disabled}
        className={cn(
          'flex w-full items-center gap-2 text-sm font-medium transition-colors',
          disabled
            ? 'text-muted-foreground cursor-not-allowed'
            : 'text-muted-foreground hover:text-foreground',
        )}
      >
        {showThankYou ? (
          <Check className="h-4 w-4 text-green-600" />
        ) : (
          <Megaphone className="h-4 w-4" />
        )}
        <span className="flex-1 text-left">
          {showThankYou ? 'Thank you!' : 'Internal feedback'}
        </span>
        {!disabled &&
          !showThankYou &&
          (expanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          ))}
      </button>

      {/* Expanded form */}
      {expanded && !disabled && (
        <form onSubmit={handleSubmit} className="mt-3 space-y-3">
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={
              'For example: this question is not relevant in this chapter.\n\n(This information is only internally visible)'
            }
            rows={4}
            className="min-h-24"
            disabled={createFeedback.isPending}
          />
          <div className="flex items-center gap-2">
            <Button
              type="submit"
              size="sm"
              disabled={!message.trim() || createFeedback.isPending}
            >
              {createFeedback.isPending ? 'Submitting...' : 'Submit feedback'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              disabled={createFeedback.isPending}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
